
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Device, DeviceEditValues } from './DeviceTypes';
import { allDevices } from './data/deviceData';
import { formatDate } from './utils/deviceUtils';

export const useDeviceList = () => {
  const [devices, setDevices] = useState<Device[]>(allDevices);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingDevice, setEditingDevice] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<DeviceEditValues>({});
  const { toast } = useToast();

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredDevices = devices.filter(device => 
    device.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.serial.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEditDevice = (device: Device) => {
    setEditingDevice(device.id);
    setEditValues({ ...device });
  };

  const handleSaveEdit = () => {
    if (!editValues.name || !editValues.category || !editValues.serial) {
      toast({
        title: "Validation Error",
        description: "All fields are required",
        variant: "destructive"
      });
      return;
    }

    setDevices(devices.map(device => 
      device.id === editingDevice ? { ...device, ...editValues } as Device : device
    ));
    
    setEditingDevice(null);
    
    toast({
      title: "Device Updated",
      description: "The device has been successfully updated."
    });
  };

  const handleCancelEdit = () => {
    setEditingDevice(null);
    setEditValues({});
  };

  const handleDeleteDevice = (deviceId: string) => {
    if (window.confirm("Are you sure you want to delete this device?")) {
      setDevices(devices.filter(device => device.id !== deviceId));
      toast({
        title: "Device Deleted",
        description: "The device has been removed from the system."
      });
    }
  };

  const handleToggleStatus = (deviceId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    setDevices(devices.map(device => 
      device.id === deviceId ? {...device, status: newStatus} : device
    ));
    
    toast({
      title: "Status Updated",
      description: `Device status changed to ${newStatus}.`
    });
  };

  const handleInputChange = (field: keyof Device, value: string) => {
    setEditValues({
      ...editValues,
      [field]: value
    });
  };

  return {
    devices,
    filteredDevices,
    searchQuery,
    editingDevice,
    editValues,
    handleSearch,
    handleEditDevice,
    handleSaveEdit,
    handleCancelEdit,
    handleDeleteDevice,
    handleToggleStatus,
    handleInputChange,
    formatDate
  };
};
